import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '@fitness-force/environments';
import { AppErrors, NotFoundError } from '@fitness-force/models';;
import type { BiostartEnrollmentStatusResponse, BiostartDevicesResponse, BiostartScanRequest, BiostartScanResponse, BiostartEnrollmentSaveRequest, BiostartClearEnrollmentRequest, BiostartDevice } from '@fitness-force/models';
import { CONST_URL_SEGEMENTS } from '@fitness-force/constants';

@Injectable({
  providedIn: 'root',
})
export class BiostartEnrollmentService {
  private http = inject(HttpClient);


  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    }),
  };

  /**
   * Check Member Enrolment Status in BioStar
   * GET /tenants/{tenant_id}/clients/{client_id}/suprema_enrolment
   */
  getEnrollmentStatus(tenantId: number, clientId: number): Observable<BiostartEnrollmentStatusResponse> {
    return this.http
      .get<BiostartEnrollmentStatusResponse>(
        environment.BASE_URL + CONST_URL_SEGEMENTS.BIOSTAR_ENROLLMENT.GET_ENROLLMENT_STATUS
          .replace('{tenant_id}', tenantId.toString())
          .replace('{client_id}', clientId.toString()),
        this.httpOptions
      )
      .pipe(
        map((res: BiostartEnrollmentStatusResponse) => res),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  /**
   * List Available Suprema Devices
   * GET /tenants/{tenant_id}/suprema_devices
   */
  getSupremaDevices(tenantId: number): Observable<BiostartDevice[]> {
    return this.http
      .get<BiostartDevice[]>(
        environment.BASE_URL + CONST_URL_SEGEMENTS.BIOSTAR_ENROLLMENT.GET_SUPREMA_DEVICES
          .replace('{tenant_id}', tenantId.toString()),
        this.httpOptions
      )
      .pipe(
        map((res: BiostartDevice[]) => res),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  /**
   * Initiate Credential Scan on Device
   * POST /tenants/{tenant_id}/suprema_devices/{device_id}/scan
   */
  scanCredential(
    tenantId: number,
    deviceId: number,
    clientId: number,
    scanRequest: BiostartScanRequest
  ): Observable<BiostartScanResponse> {
    return this.http
      .post<BiostartScanResponse>(
        environment.BASE_URL + CONST_URL_SEGEMENTS.BIOSTAR_ENROLLMENT.SCAN_CREDENTIAL
          .replace('{tenant_id}', tenantId.toString())
          .replace('{device_id}', deviceId.toString())
          .replace('{client_id}', clientId.toString()),
        scanRequest,
        this.httpOptions
      )
      .pipe(
        map((res: BiostartScanResponse) => res),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  /**
   * Save/Register Enrolment to Member
   * POST /tenants/{tenant_id}/clients/{client_id}/suprema_enrol
   */
  saveEnrollment(
    tenantId: number,
    clientId: number,
    enrollmentData: BiostartEnrollmentSaveRequest
  ): Observable<any> {
    return this.http
      .post(
        environment.BASE_URL + CONST_URL_SEGEMENTS.BIOSTAR_ENROLLMENT.SAVE_ENROLLMENT
          .replace('{tenant_id}', tenantId.toString())
          .replace('{client_id}', clientId.toString()),
        enrollmentData,
        this.httpOptions
      )
      .pipe(
        map((res: any) => res),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  /**
   * Clear Enrolment from Member
   * DELETE /tenants/{tenant_id}/clients/{client_id}/suprema_enrolment
   */
  clearEnrollment(
    tenantId: number,
    clientId: number,
    type: string
  ): Observable<any> {
    return this.http
      .request('DELETE',
        environment.BASE_URL + CONST_URL_SEGEMENTS.BIOSTAR_ENROLLMENT.CLEAR_ENROLLMENT
          .replace('{tenant_id}', tenantId.toString())
          .replace('{client_id}', clientId.toString()) + `?enrollment_type=${type}`,
        {
          headers: this.httpOptions.headers,
        }
      )
      .pipe(
        map((res: any) => res),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }
} 